import { generateId } from './index';
import { NodeFactory } from '../services/node/NodeFactory';
import type {
  CatalogComponent,
  C4Level,
  DiagramPage,
  DiagramType,
  Edge,
  Graph,
  Layer,
  Node,
  NodeGroup,
  NodeMeta,
  SequenceFragment,
  SequenceMessage,
  SequenceParticipant,
  SequenceParticipantGroup,
} from '../types';

export const CATALOG_ELIGIBLE_TYPES = new Set([
  'service',
  'database',
  'api',
  'person',
  'system',
  'queue',
  'group',
  'external',
  'logic',
  'decision',
]);

const DEFAULT_LAYER: Layer = {
  id: 'default',
  name: 'Default',
  color: '#6366f1',
  visible: true,
  dimmed: false,
};

const ROOT_GRAPH_ID = 'root';

export interface CatalogDocument {
  catalog: CatalogComponent[];
  nodes: Node[];
  participants: SequenceParticipant[];
  pages: DiagramPage[];
  graphs: Record<string, Graph>;
}

export function isCatalogEligible(type: string): boolean {
  return CATALOG_ELIGIBLE_TYPES.has(type);
}

export function defaultC4Level(type: string): C4Level {
  if (type === 'person' || type === 'system' || type === 'external') return 'context';
  if (type === 'logic' || type === 'decision') return 'component';
  return 'container';
}

function cloneLinks(links?: CatalogComponent['links']): CatalogComponent['links'] {
  return links?.map(link => ({ ...link }));
}

export function catalogEntryFromNode(node: Node, id: string): CatalogComponent {
  return {
    id,
    name: node.label,
    type: node.type,
    c4Level: defaultC4Level(node.type),
    description: node.meta?.description,
    owner: node.meta?.owner,
    status: node.meta?.status,
    links: cloneLinks(node.meta?.links),
    color: typeof node.data?.color === 'string' ? node.data.color : undefined,
  };
}

export function catalogEntryFromParticipant(participant: SequenceParticipant, id: string): CatalogComponent {
  const type = participant.type || 'service';
  return {
    id,
    name: participant.label,
    type,
    c4Level: defaultC4Level(type),
    description: participant.description,
    links: cloneLinks(participant.links),
    color: participant.color,
  };
}

export function findCatalogMatch(
  catalog: CatalogComponent[],
  name: string,
  type: string,
): CatalogComponent | undefined {
  const needle = name.trim().toLowerCase();
  if (!needle) return undefined;
  const typeNeedle = type.trim().toLowerCase();
  if (typeNeedle) {
    return catalog.find(entry =>
      entry.name.trim().toLowerCase() === needle && entry.type.trim().toLowerCase() === typeNeedle,
    );
  }
  return catalog.find(entry => entry.name.trim().toLowerCase() === needle);
}

export function applyCatalogToNode(node: Node, entry: CatalogComponent): void {
  node.label = entry.name;
  node.catalogId = entry.id;
  node.meta = {
    ...node.meta,
    description: entry.description,
    owner: entry.owner,
    status: entry.status,
    links: cloneLinks(entry.links),
  };
  if (entry.color) {
    node.data = { ...node.data, color: entry.color };
  }
}

export function applyCatalogToParticipant(participant: SequenceParticipant, entry: CatalogComponent): void {
  participant.label = entry.name;
  participant.catalogId = entry.id;
  participant.description = entry.description;
  participant.links = cloneLinks(entry.links);
  if (entry.color) participant.color = entry.color;
  if (entry.type) participant.type = entry.type;
}

export function mergeIntoCatalog(entry: CatalogComponent, patch: Partial<CatalogComponent>): void {
  if (patch.name !== undefined) entry.name = patch.name;
  if (patch.type !== undefined) entry.type = patch.type;
  if (patch.c4Level !== undefined) entry.c4Level = patch.c4Level;
  if (patch.description !== undefined) entry.description = patch.description;
  if (patch.owner !== undefined) entry.owner = patch.owner;
  if (patch.status !== undefined) entry.status = patch.status;
  if (patch.links !== undefined) entry.links = cloneLinks(patch.links);
  if (patch.tech !== undefined) entry.tech = patch.tech;
  if (patch.color !== undefined) entry.color = patch.color;
}

function walkNodes(state: CatalogDocument, visit: (node: Node) => void): void {
  state.nodes.forEach(visit);
  Object.values(state.graphs).forEach(graph => graph.nodes.forEach(visit));
  state.pages.forEach(page => {
    page.nodes.forEach(visit);
    if (page.graphs) Object.values(page.graphs).forEach(graph => graph.nodes.forEach(visit));
  });
}

function walkParticipants(state: CatalogDocument, visit: (participant: SequenceParticipant) => void): void {
  state.participants.forEach(visit);
  state.pages.forEach(page => (page.participants ?? []).forEach(visit));
}

export function syncCatalogAcrossDocument(state: CatalogDocument, catalogId: string): void {
  const entry = state.catalog.find(item => item.id === catalogId);
  if (!entry) return;
  walkNodes(state, node => {
    if (node.catalogId === catalogId) applyCatalogToNode(node, entry);
  });
  walkParticipants(state, participant => {
    if (participant.catalogId === catalogId) applyCatalogToParticipant(participant, entry);
  });
}

export function countCatalogViews(state: CatalogDocument, catalogId: string): number {
  const seen = new Set<string>();
  walkNodes(state, node => {
    if (node.catalogId === catalogId) seen.add(`n:${node.id}`);
  });
  walkParticipants(state, participant => {
    if (participant.catalogId === catalogId) seen.add(`p:${participant.id}`);
  });
  return seen.size;
}

export function enrollNode(state: CatalogDocument, node: Node, idFn: (prefix: string) => string = generateId): void {
  if (!isCatalogEligible(node.type)) return;
  if (node.catalogId) {
    if (!state.catalog.some(entry => entry.id === node.catalogId)) {
      state.catalog.push(catalogEntryFromNode(node, node.catalogId));
    }
    return;
  }
  const match = findCatalogMatch(state.catalog, node.label, node.type);
  if (match) {
    node.catalogId = match.id;
    if (!match.description && node.meta?.description) match.description = node.meta.description;
    if (!match.owner && node.meta?.owner) match.owner = node.meta.owner;
    if (!match.status && node.meta?.status) match.status = node.meta.status;
    if ((!match.links || match.links.length === 0) && node.meta?.links?.length) {
      match.links = cloneLinks(node.meta.links);
    }
    return;
  }
  const entry = catalogEntryFromNode(node, idFn('cat'));
  state.catalog.push(entry);
  node.catalogId = entry.id;
}

export function enrollParticipant(
  state: CatalogDocument,
  participant: SequenceParticipant,
  idFn: (prefix: string) => string = generateId,
): void {
  const type = participant.type || 'service';
  if (!isCatalogEligible(type)) return;
  if (participant.catalogId) {
    if (!state.catalog.some(entry => entry.id === participant.catalogId)) {
      state.catalog.push(catalogEntryFromParticipant(participant, participant.catalogId));
    }
    return;
  }
  const match = findCatalogMatch(state.catalog, participant.label, type);
  if (match) {
    participant.catalogId = match.id;
    if (!match.description && participant.description) match.description = participant.description;
    return;
  }
  const entry = catalogEntryFromParticipant(participant, idFn('cat'));
  state.catalog.push(entry);
  participant.catalogId = entry.id;
}

export function inferCatalogFromDocument(state: CatalogDocument, idFn: (prefix: string) => string = generateId): CatalogComponent[] {
  const next: CatalogComponent[] = state.catalog.map(entry => ({
    ...entry,
    links: cloneLinks(entry.links),
  }));
  const draft: CatalogDocument = { ...state, catalog: next };
  walkNodes(draft, node => enrollNode(draft, node, idFn));
  walkParticipants(draft, participant => enrollParticipant(draft, participant, idFn));
  return draft.catalog;
}

function makeNode(
  type: string,
  label: string,
  x: number,
  y: number,
  catalogId: string,
  idFn: (prefix: string) => string,
  extras?: Partial<Node>,
): Node {
  const blueprint = NodeFactory.create(type, 0);
  return {
    id: idFn('node'),
    type,
    label,
    position: { x, y },
    size: blueprint.size,
    sockets: blueprint.sockets,
    data: { ...blueprint.data },
    catalogId,
    ...extras,
  };
}

function connect(source: Node, target: Node, label: string, idFn: (prefix: string) => string): Edge {
  const out = source.sockets.find(socket => socket.type === 'output');
  const inn = target.sockets.find(socket => socket.type === 'input');
  return {
    id: idFn('edge'),
    sourceNodeId: source.id,
    sourceSocketId: out?.id ?? 'out-0',
    targetNodeId: target.id,
    targetSocketId: inn?.id ?? 'in-0',
    label,
  };
}

function pageGraph(nodes: Node[], edges: Edge[]): Record<string, Graph> {
  return {
    [ROOT_GRAPH_ID]: {
      id: ROOT_GRAPH_ID,
      nodes,
      edges,
      viewport: { x: 40, y: 40, scale: 1 },
    },
  };
}

function makePage(
  id: string,
  name: string,
  diagramType: DiagramType,
  description: string,
  nodes: Node[],
  edges: Edge[],
  extras?: Partial<DiagramPage>,
): DiagramPage {
  return {
    id,
    name,
    description,
    diagramType,
    nodes,
    edges,
    groups: [],
    layers: [{ ...DEFAULT_LAYER }],
    viewport: { x: 40, y: 40, scale: 1 },
    participants: extras?.participants ?? [],
    messages: extras?.messages ?? [],
    fragments: extras?.fragments ?? [],
    participantGroups: extras?.participantGroups ?? [],
    seqLifelineLength: extras?.seqLifelineLength ?? 500,
    graphs: pageGraph(nodes, edges),
  };
}

export interface BuiltC4Workspace {
  catalog: CatalogComponent[];
  pages: DiagramPage[];
  activePage: string;
  nodes: Node[];
  edges: Edge[];
  groups: NodeGroup[];
  layers: Layer[];
  viewport: { x: number; y: number; scale: number };
  graphs: Record<string, Graph>;
  activeGraphId: string;
  diagramType: DiagramType;
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  seqLifelineLength: number;
}

export function buildC4Workspace(
  systemName: string,
  idFn: (prefix: string) => string = generateId,
): BuiltC4Workspace {
  const name = systemName.trim() || 'System';
  const userId = idFn('cat');
  const systemId = idFn('cat');
  const externalId = idFn('cat');
  const apiId = idFn('cat');
  const appId = idFn('cat');
  const dbId = idFn('cat');

  const catalog: CatalogComponent[] = [
    { id: userId, name: 'User', type: 'person', c4Level: 'context' },
    { id: systemId, name, type: 'system', c4Level: 'context', description: `System context for ${name}` },
    { id: externalId, name: 'External System', type: 'external', c4Level: 'context' },
    { id: apiId, name: 'API Gateway', type: 'api', c4Level: 'container' },
    { id: appId, name: 'Application', type: 'service', c4Level: 'container' },
    { id: dbId, name: 'Database', type: 'database', c4Level: 'container' },
  ];

  const contextId = idFn('page');
  const containersId = idFn('page');
  const sequenceId = idFn('page');

  const user = makeNode('person', 'User', 80, 220, userId, idFn);
  const system = makeNode('system', name, 360, 200, systemId, idFn, { linkedPageId: containersId });
  const external = makeNode('external', 'External System', 680, 220, externalId, idFn);
  const contextNodes = [user, system, external];
  const contextEdges = [
    connect(user, system, 'uses', idFn),
    connect(system, external, 'uses', idFn),
  ];

  const api = makeNode('api', 'API Gateway', 80, 200, apiId, idFn);
  const app = makeNode('service', 'Application', 360, 200, appId, idFn);
  const db = makeNode('database', 'Database', 640, 200, dbId, idFn);
  const containerNodes = [api, app, db];
  const containerEdges = [
    connect(api, app, 'HTTPS', idFn),
    connect(app, db, 'SQL', idFn),
  ];

  const userP: SequenceParticipant = {
    id: idFn('participant'), type: 'person', label: 'User', color: '#ec4899', order: 0, x: 150, catalogId: userId,
  };
  const apiP: SequenceParticipant = {
    id: idFn('participant'), type: 'api', label: 'API Gateway', color: '#6366f1', order: 1, x: 350, catalogId: apiId,
  };
  const appP: SequenceParticipant = {
    id: idFn('participant'), type: 'service', label: 'Application', color: '#10b981', order: 2, x: 550, catalogId: appId,
  };
  const dbP: SequenceParticipant = {
    id: idFn('participant'), type: 'database', label: 'Database', color: '#f59e0b', order: 3, x: 750, catalogId: dbId,
  };
  const participants: SequenceParticipant[] = [userP, apiP, appP, dbP];
  const seqMsg = (
    source: SequenceParticipant,
    target: SequenceParticipant,
    y: number,
    label: string,
    type: SequenceMessage['type'],
  ): SequenceMessage => ({
    id: idFn('message'),
    sourceId: source.id,
    targetId: target.id,
    y,
    label,
    type,
  });
  const messages: SequenceMessage[] = [
    seqMsg(userP, apiP, 160, 'Request', 'sync'),
    seqMsg(apiP, appP, 240, 'Handle', 'sync'),
    seqMsg(appP, dbP, 320, 'Query', 'sync'),
    seqMsg(dbP, appP, 400, 'Rows', 'return'),
    seqMsg(appP, apiP, 480, 'Result', 'return'),
    seqMsg(apiP, userP, 560, 'Response', 'return'),
  ];

  const contextPage = makePage(
    contextId,
    'System Context',
    'flow',
    `C4 Level 1 — people and systems around ${name}`,
    contextNodes,
    contextEdges,
  );
  const containersPage = makePage(
    containersId,
    'Containers',
    'flow',
    `C4 Level 2 — containers inside ${name}`,
    containerNodes,
    containerEdges,
  );
  const sequencePage = makePage(
    sequenceId,
    'Sequence',
    'sequence',
    `A first interaction across ${name}`,
    [],
    [],
    { participants, messages, seqLifelineLength: 720 },
  );

  return {
    catalog,
    pages: [contextPage, containersPage, sequencePage],
    activePage: contextId,
    nodes: contextNodes,
    edges: contextEdges,
    groups: [],
    layers: [{ ...DEFAULT_LAYER }],
    viewport: { x: 40, y: 40, scale: 1 },
    graphs: pageGraph(contextNodes, contextEdges),
    activeGraphId: ROOT_GRAPH_ID,
    diagramType: 'flow',
    participants: [],
    messages: [],
    fragments: [],
    participantGroups: [],
    seqLifelineLength: 500,
  };
}

export function applyWorkspaceToState<T extends CatalogDocument & {
  activePage: string;
  activeGraphId: string;
  diagramType: DiagramType;
  edges: Edge[];
  groups: NodeGroup[];
  layers: Layer[];
  viewport: { x: number; y: number; scale: number };
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  participantGroups: SequenceParticipantGroup[];
  seqLifelineLength: number;
  selectedItems: string[];
  graphNavStack: unknown[];
  groupNavStack: unknown[];
  flows?: unknown[];
}>(state: T, built: BuiltC4Workspace): void {
  state.catalog = built.catalog;
  state.pages = built.pages;
  state.activePage = built.activePage;
  state.activeGraphId = built.activeGraphId;
  state.diagramType = built.diagramType;
  state.nodes = built.nodes;
  state.edges = built.edges;
  state.groups = built.groups;
  state.layers = built.layers;
  state.viewport = built.viewport;
  state.graphs = built.graphs;
  state.participants = built.participants;
  state.messages = built.messages;
  state.fragments = built.fragments;
  state.participantGroups = built.participantGroups;
  state.seqLifelineLength = built.seqLifelineLength;
  state.selectedItems = [];
  state.graphNavStack = [];
  state.groupNavStack = [];
  if ('flows' in state) state.flows = [];
}

export function nodeMetaFromCatalog(entry: CatalogComponent): NodeMeta {
  return {
    description: entry.description,
    owner: entry.owner,
    status: entry.status,
    links: cloneLinks(entry.links),
  };
}
